import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Filter,
  ExternalLink,
  Building2,
  Calendar,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  FileCheck,
  XCircle,
  X,
  Upload,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { STATUS_STYLES, ALL_STATUSES, getStatusStyle } from '@/lib/orderStatusStyles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const formatAmount = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const formatLongDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = d.getUTCDate();
  const j = day % 10, k = day % 100;
  const suffix = (k >= 11 && k <= 13) ? 'th' : j === 1 ? 'st' : j === 2 ? 'nd' : j === 3 ? 'rd' : 'th';
  return `${day}${suffix} ${d.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' })} ${d.getUTCFullYear()}`;
};

const SuperAdminOrders = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'review'

  // Per-row action state for the review tab
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [feeInputs, setFeeInputs] = useState({}); // {orderId: "120"}
  const [actionMsg, setActionMsg] = useState({}); // {orderId: "..."}

  // Modal state
  const [markFiledOrder, setMarkFiledOrder] = useState(null); // order object
  const [markFiledFile, setMarkFiledFile] = useState(null);
  const [markFiledNote, setMarkFiledNote] = useState('');
  const [markFiledSubmitting, setMarkFiledSubmitting] = useState(false);
  const [markFiledError, setMarkFiledError] = useState('');

  const [cancelOrder, setCancelOrder] = useState(null); // order object
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNote, setCancelNote] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState('');

  const [toast, setToast] = useState(''); // success toast text
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4500);
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(response.data.orders || []);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const reviewOrders = orders.filter((o) => o.status === 'pending_review');

  const filteredOrders = orders.filter((order) => {
    const s = searchTerm.toLowerCase();
    const matches =
      order.id?.toLowerCase().includes(s) ||
      order.user_email?.toLowerCase().includes(s) ||
      order.user_name?.toLowerCase().includes(s) ||
      order.company_name?.toLowerCase().includes(s) ||
      order.product_name?.toLowerCase().includes(s);
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matches && matchesStatus;
  });

  const statusOptions = ['all', ...ALL_STATUSES];

  const handleApproveFixed = async (orderId) => {
    setActionLoadingId(orderId);
    setActionMsg((m) => ({ ...m, [orderId]: '' }));
    try {
      await axios.post(`${API}/admin/orders/${orderId}/approve-filing`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActionMsg((m) => ({ ...m, [orderId]: 'Approved — client notified' }));
      await fetchOrders();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Approval failed';
      setActionMsg((m) => ({ ...m, [orderId]: `Error: ${detail}` }));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleConfirmVariable = async (orderId) => {
    const raw = (feeInputs[orderId] || '').toString().trim();
    const fee = parseFloat(raw);
    if (!Number.isFinite(fee) || fee <= 0) {
      setActionMsg((m) => ({ ...m, [orderId]: 'Enter a government fee greater than 0' }));
      return;
    }
    setActionLoadingId(orderId);
    setActionMsg((m) => ({ ...m, [orderId]: '' }));
    try {
      await axios.patch(`${API}/admin/orders/${orderId}/confirm-govt-fee`, { govt_fee: fee }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActionMsg((m) => ({ ...m, [orderId]: 'Fee confirmed — client notified' }));
      await fetchOrders();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Confirm failed';
      setActionMsg((m) => ({ ...m, [orderId]: `Error: ${detail}` }));
    } finally {
      setActionLoadingId(null);
    }
  };

  const openMarkFiled = (order) => {
    setMarkFiledOrder(order);
    setMarkFiledFile(null);
    setMarkFiledNote('');
    setMarkFiledError('');
  };
  const closeMarkFiled = () => {
    setMarkFiledOrder(null);
    setMarkFiledFile(null);
    setMarkFiledNote('');
    setMarkFiledError('');
    setMarkFiledSubmitting(false);
  };
  const submitMarkFiled = async () => {
    if (!markFiledOrder) return;
    if (!markFiledFile) {
      setMarkFiledError('Acknowledgement PDF is required');
      return;
    }
    setMarkFiledSubmitting(true);
    setMarkFiledError('');
    try {
      const fd = new FormData();
      fd.append('file', markFiledFile);
      if (markFiledNote.trim()) fd.append('note', markFiledNote.trim());
      await axios.post(`${API}/admin/orders/${markFiledOrder.id}/mark-filed`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      showToast('Order marked as filed');
      closeMarkFiled();
      await fetchOrders();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to mark as filed';
      setMarkFiledError(typeof detail === 'string' ? detail : 'Failed to mark as filed');
    } finally {
      setMarkFiledSubmitting(false);
    }
  };

  const openCancel = (order) => {
    setCancelOrder(order);
    setCancelReason('');
    setCancelNote('');
    setCancelError('');
  };
  const closeCancel = () => {
    setCancelOrder(null);
    setCancelReason('');
    setCancelNote('');
    setCancelError('');
    setCancelSubmitting(false);
  };
  const submitCancel = async () => {
    if (!cancelOrder) return;
    if (!cancelReason) {
      setCancelError('Reason is required');
      return;
    }
    setCancelSubmitting(true);
    setCancelError('');
    try {
      await axios.post(`${API}/admin/orders/${cancelOrder.id}/cancel-filing`, {
        reason: cancelReason,
        note: cancelNote.trim() || undefined,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast('Order cancelled — client notified');
      closeCancel();
      await fetchOrders();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Cancel failed';
      setCancelError(typeof detail === 'string' ? detail : 'Cancel failed');
    } finally {
      setCancelSubmitting(false);
    }
  };

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#00E87A] animate-spin" />
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Orders</h1>
          <p className="text-gray-400 text-sm mt-1">{orders.length} total · {reviewOrders.length} awaiting review</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-800 mb-6" data-testid="orders-tabs">
        <button
          onClick={() => setActiveTab('all')}
          data-testid="orders-tab-all"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'all'
              ? 'border-[#00E87A] text-[#00E87A]'
              : 'border-transparent text-gray-400 hover:text-gray-200'
          }`}
        >
          All Orders ({orders.length})
        </button>
        <button
          onClick={() => setActiveTab('review')}
          data-testid="orders-tab-review"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
            activeTab === 'review'
              ? 'border-amber-400 text-amber-400'
              : 'border-transparent text-gray-400 hover:text-gray-200'
          }`}
        >
          Review Orders
          {reviewOrders.length > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-semibold">
              {reviewOrders.length}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'all' && (
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search by order ID, email, company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-[#161B22] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#00E87A]"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2.5 bg-[#161B22] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A]"
                data-testid="status-filter"
              >
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status === 'all' ? 'All Statuses' : STATUS_STYLES[status]?.label || status}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Orders Table */}
          <div className="bg-[#161B22] border border-gray-800 rounded-lg overflow-hidden">
            {filteredOrders.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-gray-400">No orders found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Order ID</th>
                      <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Client</th>
                      <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Service</th>
                      <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Company</th>
                      <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Amount</th>
                      <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Status</th>
                      <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Date</th>
                      <th className="text-right text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {filteredOrders.map((order) => {
                      const ss = getStatusStyle(order.status);
                      return (
                        <tr key={order.id} className="hover:bg-gray-800/30 transition-colors">
                          <td className="px-4 py-3">
                            <span className="text-gray-400 text-sm font-mono">#{order.id?.slice(0, 8)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-white text-sm">{order.user_email || '-'}</span>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <span className="text-gray-300 text-sm">{order.service_name || order.product_name || '-'}</span>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-gray-500" />
                              {order.company_id ? (
                                <Link
                                  to={`/superadmin/companies/${order.company_id}`}
                                  className="text-[#00E87A] hover:text-[#00E87A]/80 text-sm transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {order.company_name || '-'}
                                </Link>
                              ) : (
                                <span className="text-gray-300 text-sm">{order.company_name || '-'}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-white text-sm font-medium">
                              {formatAmount(order.final_amount || order.total_amount)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${ss.bg} ${ss.text}`}>
                              {ss.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <div className="flex items-center gap-1 text-gray-400 text-sm">
                              <Calendar className="w-4 h-4" />
                              {formatDate(order.created_at)}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Link
                              to={`/superadmin/orders/${order.id}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-[#00E87A] hover:bg-[#00E87A]/10 rounded-lg transition-colors"
                              data-testid={`view-order-${order.id}`}
                            >
                              View
                              <ExternalLink className="w-4 h-4" />
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'review' && (
        <div className="bg-[#161B22] border border-gray-800 rounded-lg overflow-hidden" data-testid="review-orders-panel">
          {reviewOrders.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle2 className="w-10 h-10 text-[#00E87A] mx-auto mb-3" />
              <p className="text-gray-300 text-base font-medium">No orders awaiting review</p>
              <p className="text-gray-500 text-sm mt-1">Auto-generated filing orders appear here at the 90-day mark.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Company</th>
                    <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Filing</th>
                    <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden lg:table-cell">State</th>
                    <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Entity</th>
                    <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Due Date</th>
                    <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Fee Type</th>
                    <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Govt Fee</th>
                    <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Created</th>
                    <th className="text-right text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {reviewOrders.map((order) => {
                    const ci = order.collected_information || {};
                    const isFixed = (order.fee_type || ci.fee_type) === 'fixed';
                    const due = ci.due_date || order.govt_deadline;
                    const filingName = ci.state_filing_name || order.product_name || 'Annual filing';
                    const stateLabel = ci.state_name || ci.state_code || '-';
                    const entity = (ci.entity_type || '').replace('us_', '').toUpperCase() || '-';
                    return (
                      <tr key={order.id} className="hover:bg-gray-800/30 transition-colors align-top" data-testid={`review-order-row-${order.id}`}>
                        <td className="px-4 py-3">
                          {order.company_id ? (
                            <Link to={`/superadmin/companies/${order.company_id}`} className="text-[#00E87A] hover:text-[#00E87A]/80 text-sm">
                              {order.company_name || '-'}
                            </Link>
                          ) : (
                            <span className="text-gray-300 text-sm">{order.company_name || '-'}</span>
                          )}
                          <p className="text-xs text-gray-500 font-mono mt-0.5">#{order.id?.slice(0, 8)}</p>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-gray-200 text-sm">{filingName}</span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className="text-gray-300 text-sm">{stateLabel}</span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className="text-gray-300 text-sm">{entity}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-white text-sm">{formatLongDate(due)}</span>
                        </td>
                        <td className="px-4 py-3">
                          {isFixed ? (
                            <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-300">Fixed</span>
                          ) : (
                            <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-300 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />Variable
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isFixed ? (
                            <span className="text-white text-sm font-mono">${Number(order.govt_fee || 0).toFixed(2)}</span>
                          ) : (
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={feeInputs[order.id] ?? ''}
                              onChange={(e) => setFeeInputs((m) => ({ ...m, [order.id]: e.target.value }))}
                              className="w-24 px-2 py-1 text-sm bg-[#0D1117] border border-gray-700 rounded text-white focus:outline-none focus:border-[#00E87A]"
                              data-testid={`govt-fee-input-${order.id}`}
                            />
                          )}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-gray-400 text-sm">{formatDate(order.created_at)}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex flex-col items-end gap-1.5">
                            {isFixed ? (
                              <button
                                onClick={() => handleApproveFixed(order.id)}
                                disabled={actionLoadingId === order.id}
                                data-testid={`approve-filing-btn-${order.id}`}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-[#00E87A] text-[#0F141B] hover:bg-[#00d171] disabled:opacity-50 w-full justify-center"
                              >
                                {actionLoadingId === order.id ? 'Approving…' : 'Approve & Notify Client'}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleConfirmVariable(order.id)}
                                disabled={actionLoadingId === order.id}
                                data-testid={`confirm-fee-btn-${order.id}`}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-amber-500 text-[#0F141B] hover:bg-amber-400 disabled:opacity-50 w-full justify-center"
                              >
                                {actionLoadingId === order.id ? 'Saving…' : 'Confirm Fee & Notify Client'}
                              </button>
                            )}
                            <button
                              onClick={() => openMarkFiled(order)}
                              data-testid={`mark-filed-btn-${order.id}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-gray-200 border border-gray-600 hover:bg-gray-800 w-full justify-center"
                            >
                              <FileCheck className="w-3 h-3" /> Mark as Already Filed
                            </button>
                            <button
                              onClick={() => openCancel(order)}
                              data-testid={`cancel-filing-btn-${order.id}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-red-300 border border-red-500/40 hover:bg-red-500/10 w-full justify-center"
                            >
                              <XCircle className="w-3 h-3" /> Cancel Order
                            </button>
                            {actionMsg[order.id] && (
                              <span
                                className={`text-xs ${actionMsg[order.id].startsWith('Error') ? 'text-red-400' : 'text-emerald-400'}`}
                                data-testid={`review-action-msg-${order.id}`}
                              >
                                {actionMsg[order.id]}
                              </span>
                            )}
                            <Link
                              to={`/superadmin/orders/${order.id}`}
                              className="text-xs text-gray-400 hover:text-gray-200 inline-flex items-center gap-1"
                              data-testid={`view-review-order-${order.id}`}
                            >
                              View detail <ExternalLink className="w-3 h-3" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {/* Mark as Already Filed Modal */}
      {markFiledOrder && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="mark-filed-modal">
          <div className="bg-[#0D1117] border border-gray-700 rounded-xl w-full max-w-md p-6 relative">
            <button
              onClick={closeMarkFiled}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-200"
              data-testid="mark-filed-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold text-white mb-1">Mark as Already Filed</h3>
            <p className="text-sm text-gray-400 mb-4">
              {markFiledOrder.company_name} — {markFiledOrder.product_name}
            </p>

            <label className="block text-sm text-gray-300 mb-1">
              Upload Acknowledgement (PDF) <span className="text-red-400">*</span>
            </label>
            <label className="block border border-dashed border-gray-700 rounded-lg px-4 py-6 text-center cursor-pointer hover:border-[#00E87A] mb-4" data-testid="mark-filed-dropzone">
              <Upload className="w-5 h-5 text-gray-500 mx-auto mb-2" />
              <span className="text-sm text-gray-400">
                {markFiledFile ? markFiledFile.name : 'Click to choose a file'}
              </span>
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => setMarkFiledFile(e.target.files?.[0] || null)}
                data-testid="mark-filed-file-input"
              />
            </label>

            <label className="block text-sm text-gray-300 mb-1">Note (optional)</label>
            <textarea
              value={markFiledNote}
              onChange={(e) => setMarkFiledNote(e.target.value)}
              rows={3}
              placeholder='e.g. "Filed via bank transfer on May 10"'
              className="w-full px-3 py-2 bg-[#161B22] border border-gray-700 rounded-md text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#00E87A] mb-2"
              data-testid="mark-filed-note"
            />

            {markFiledError && (
              <p className="text-sm text-red-400 mb-3" data-testid="mark-filed-error">{markFiledError}</p>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={closeMarkFiled}
                disabled={markFiledSubmitting}
                data-testid="mark-filed-cancel"
                className="px-4 py-2 rounded-md text-sm text-gray-300 border border-gray-700 hover:bg-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={submitMarkFiled}
                disabled={markFiledSubmitting}
                data-testid="mark-filed-submit"
                className="px-4 py-2 rounded-md text-sm font-medium bg-[#00E87A] text-[#0F141B] hover:bg-[#00d171] disabled:opacity-50"
              >
                {markFiledSubmitting ? 'Submitting…' : 'Confirm & Complete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Order Modal */}
      {cancelOrder && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="cancel-filing-modal">
          <div className="bg-[#0D1117] border border-gray-700 rounded-xl w-full max-w-md p-6 relative">
            <button
              onClick={closeCancel}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-200"
              data-testid="cancel-filing-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold text-white mb-1">Cancel Filing Order</h3>
            <p className="text-sm text-gray-400 mb-4">
              {cancelOrder.company_name} — {cancelOrder.product_name}
            </p>

            <label className="block text-sm text-gray-300 mb-1">
              Reason <span className="text-red-400">*</span>
            </label>
            <select
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="w-full px-3 py-2 bg-[#161B22] border border-gray-700 rounded-md text-white text-sm focus:outline-none focus:border-[#00E87A] mb-4"
              data-testid="cancel-filing-reason"
            >
              <option value="">— Select a reason —</option>
              <option value="client_will_file_independently">Client will file independently</option>
              <option value="client_filed_already">Client filed already</option>
              <option value="other">Other</option>
            </select>

            <label className="block text-sm text-gray-300 mb-1">Note (optional)</label>
            <textarea
              value={cancelNote}
              onChange={(e) => setCancelNote(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-[#161B22] border border-gray-700 rounded-md text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#00E87A] mb-2"
              data-testid="cancel-filing-note"
            />

            {cancelError && (
              <p className="text-sm text-red-400 mb-3" data-testid="cancel-filing-error">{cancelError}</p>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={closeCancel}
                disabled={cancelSubmitting}
                data-testid="cancel-filing-back"
                className="px-4 py-2 rounded-md text-sm text-gray-300 border border-gray-700 hover:bg-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={submitCancel}
                disabled={cancelSubmitting}
                data-testid="cancel-filing-submit"
                className="px-4 py-2 rounded-md text-sm font-medium bg-red-500 text-white hover:bg-red-400 disabled:opacity-50"
              >
                {cancelSubmitting ? 'Cancelling…' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#00E87A] text-[#0F141B] px-4 py-2.5 rounded-md shadow-lg text-sm font-medium" data-testid="review-orders-toast">
          {toast}
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminOrders;
